import type { ReactNode } from "react";
import { cx } from "../../cx";
import { AlertCircleIcon, CheckCircleIcon, InfoCircleIcon } from "../../rtl/icons";

export type AlertTone = "info" | "success" | "warning" | "error";

/** Tone colours — the same semantic pairs as `Toast` (contrast-tested tokens). */
export const alertToneClasses: Readonly<Record<AlertTone, { box: string; icon: string }>> = {
  info: { box: "bg-info-bg text-info-fg border-info-icon", icon: "text-info-icon" },
  success: { box: "bg-success-bg text-success-fg border-success-icon", icon: "text-success-icon" },
  warning: { box: "bg-warning-bg text-warning-fg border-warning-icon", icon: "text-warning-icon" },
  error: { box: "bg-danger-bg text-danger-bg-fg border-danger-icon", icon: "text-danger-icon" },
};

const ICON: Readonly<Record<AlertTone, typeof InfoCircleIcon>> = {
  info: InfoCircleIcon,
  success: CheckCircleIcon,
  warning: AlertCircleIcon,
  error: AlertCircleIcon,
};

export interface AlertProps {
  readonly tone?: AlertTone;
  readonly title?: string;
  readonly children?: ReactNode;
  /** Actions (buttons/links) at the end of the alert. */
  readonly actions?: ReactNode;
  readonly className?: string;
}

/**
 * Inline, persistent message inside a page or form (unlike `Toast`, it does not disappear).
 * Errors use `role="alert"`; the other tones `role="status"`. The accent border sits on the
 * inline-start edge.
 */
export function Alert({ tone = "info", title, children, actions, className }: AlertProps) {
  const Icon = ICON[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cx(
        "flex items-start gap-3 rounded-xl border border-s-4 p-4",
        alertToneClasses[tone].box,
        className,
      )}
    >
      <Icon className={cx("mt-0.5", alertToneClasses[tone].icon)} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title && <p className="text-sm font-semibold">{title}</p>}
        {children && <div className="text-sm">{children}</div>}
        {actions && <div className="mt-2 flex flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}
